import { CrewMerchantUi, ShipmentMethod } from 'corso-types';

import { faQrcode } from '@fortawesome/pro-light-svg-icons';
import { Action, ButtonGroup, Card, Icon, SimpleTooltip } from 'corso-admin-ui';
import ClipboardButton from '~/components/ClipboardButton';
import CarrierIcon from '~/components/tracking/CarrierIcon';

type Shipment = NonNullable<CrewMerchantUi.ClaimShipment>;

const statusLabels = {
  delivered: 'Delivered',
  available_for_pickup: 'Available for Pickup',
  cancelled: 'Cancelled',
  error: 'Error',
  failure: 'Failure',
  in_transit: 'In Transit',
  out_for_delivery: 'Out for Delivery',
  pre_transit: 'Getting Ready To Ship',
  return_to_sender: 'Return to Sender',
  unknown: 'Getting Ready To Ship',
} satisfies Record<
  CrewMerchantUi.ReturnShipmentTracker['latestStatus'],
  string
>;

export default function ShipmentLabel({ shipment }: { shipment: Shipment }) {
  if (shipment.returnShipmentType !== ShipmentMethod.label) return null;

  const { tracker, returnShipmentPdfAssetUrl, qrCodeDetail } = shipment;
  if (!tracker) return null;

  const { carrier, latestStatus } = tracker;
  const { trackingCode, name: carrierName, trackingUrl } = carrier;

  const hasDownloadUrl = returnShipmentPdfAssetUrl || qrCodeDetail?.url;
  const qrCodeUrl = qrCodeDetail?.url;
  const labelUrl = returnShipmentPdfAssetUrl;

  return (
    <div className="flex flex-col gap-4">
      {/* some space */}

      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <CarrierIcon carrierName={carrierName} />
            <Card.Heading> {carrierName} </Card.Heading>
          </div>
        </div>

        <p className="text-xs text-neutral-500">
          {' '}
          {statusLabels[latestStatus]}{' '}
        </p>

        {trackingCode && (
          <div className="flex items-center justify-between">
            <Action
              variant="link"
              target="_blank"
              className="text-xs"
              to={
                trackingUrl ?? `https://www.google.com/search?q=${trackingCode}`
              }
            >
              {trackingCode}
            </Action>
            <div className="flex gap-2">
              <ClipboardButton onClick={() => trackingCode} variant="ghost" />
            </div>
          </div>
        )}
      </div>

      {hasDownloadUrl && (
        <ButtonGroup horizontal>
          {qrCodeUrl && (
            <SimpleTooltip content="Download QR Code">
              <Action to={qrCodeUrl} accessibilityLabel="Download" download>
                <Icon icon={faQrcode} />
              </Action>
            </SimpleTooltip>
          )}

          {labelUrl && (
            <SimpleTooltip content="Download Label">
              <Action
                to={labelUrl}
                accessibilityLabel="Download"
                download
                icon="download"
              />
            </SimpleTooltip>
          )}
        </ButtonGroup>
      )}
    </div>
  );
}
